import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Upload, Input, Button, Spin, Image, Typography, Alert } from "antd";
import { ImageUp } from "lucide-react";
import useUploadMedia from "hooks/useUploadMedia/useUploadMedia";
import {
  emptyBase,
  mergeIntake,
  mapModelToFormValues,
  getMissingRequiredFields,
} from "../../commons/posterIntake.helpers";
import { fetchPosterIntake } from "../../commons/posterIntake.service";

const { Dragger } = Upload;
const { Title, Paragraph, Text } = Typography;

function PosterUploadModal({ open, onClose }) {
  const navigate = useNavigate();
  const { upload } = useUploadMedia();

  const [phase, setPhase] = useState("input"); // "input" | "review"
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [notes, setNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [refineCount, setRefineCount] = useState(0);

  const MAX_REFINES = 2;

  const pickFile = (picked) => {
    setFile(picked);
    setPreviewUrl(URL.createObjectURL(picked));
    return false; // prevent antd's auto-upload
  };

  const startOver = () => {
    setPhase("input");
    setResult(null);
    setFile(null);
    setPreviewUrl(null);
    setRefineCount(0);
  };

  // Overlay the AI intake onto the skeleton and derive seed + gaps. The BFF tags
  // any field it inherited from the last event with source "history".
  const buildResult = (base, coverUrl, intake) => {
    const model = intake ? mergeIntake(base.model, intake.event) : base.model;
    const sources = { ...base.sources, ...(intake?.sources || {}) };
    if (coverUrl) sources["/media"] = "image";
    const seed = mapModelToFormValues(model);
    return {
      coverUrl,
      base,
      hasHistory: Object.values(sources).includes("history"),
      intake,
      aiFailed: !intake,
      model,
      sources,
      seed,
      missing: getMissingRequiredFields(seed),
    };
  };

  const goToWizard = (res) => {
    navigate("/shoup-community/events/create", {
      state: {
        posterSeed: res.seed,
        posterProvenance: {
          coverUrl: res.coverUrl,
          sources: res.sources,
          model: res.model,
          summary: res.intake?.summary ?? null,
          warnings: res.intake?.warnings ?? [],
          hasHistory: res.hasHistory,
          missing: res.missing,
        },
      },
    });
  };

  const handleGenerate = async () => {
    if (!file) return;
    setProcessing(true);

    // The BFF assembles history + master data server-side, so the client only
    // uploads the cover and runs the intake in parallel.
    const [coverUrl, intake] = await Promise.all([
      upload(file).catch(() => null),
      fetchPosterIntake({ file, notes }),
    ]);

    const base = emptyBase();
    base.model.media = coverUrl ? [coverUrl] : [];

    const built = buildResult(base, coverUrl, intake);

    // Clean path: poster read fine, at most one gap, no warnings — skip the review
    // step entirely and open the wizard pre-filled (it still validates step by step).
    const warningCount = built.intake?.warnings?.length ?? 0;
    if (!built.aiFailed && built.missing.length <= 1 && warningCount === 0) {
      goToWizard(built);
      return;
    }

    setResult(built);
    setPhase("review");
    setProcessing(false);
  };

  // Re-run the single intake call with the updated notes — no re-upload, reuse base.
  const handleRefine = async () => {
    setProcessing(true);
    const intake = await fetchPosterIntake({ file, notes });
    setResult((prev) => buildResult(prev.base, prev.coverUrl, intake));
    setRefineCount((c) => c + 1);
    setProcessing(false);
  };

  const handleCreate = () => goToWizard(result);

  return (
    <Modal
      open={open}
      onCancel={processing ? undefined : onClose}
      title="Create event from a poster"
      footer={null}
      maskClosable={!processing}
      width={520}
      destroyOnClose
    >
      {processing ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
            padding: "48px 0",
          }}
        >
          <Spin size="large" />
          <Text type="secondary">
            {phase === "review"
              ? "Updating from your notes…"
              : "Reading your poster and pre-filling the form…"}
          </Text>
        </div>
      ) : phase === "review" ? (
        <div className="space-y-4">
          {result?.aiFailed ? (
            <Alert
              type="warning"
              showIcon
              message="Couldn't read the poster clearly"
              description="I filled what I could from your recent events and defaults — please review and add the rest."
            />
          ) : (
            <Paragraph style={{ marginBottom: 0 }}>
              {result?.intake?.summary ??
                "I pre-filled what I could from your poster and recent events."}
            </Paragraph>
          )}

          {result?.intake?.warnings?.map((w, i) => (
            <Alert
              key={i}
              type={w.level === "warning" ? "warning" : "info"}
              message={w.message}
              showIcon
            />
          ))}

          {result?.missing?.length > 1 && refineCount < MAX_REFINES ? (
            <Alert
              type="info"
              showIcon
              message="Still needs your input"
              description={result.missing.join(" · ")}
            />
          ) : result?.missing?.length > 1 ? (
            <Alert
              type="warning"
              showIcon
              message="I'll leave these blank"
              description={`You can complete them directly on the next screen: ${result.missing.join(" · ")}`}
            />
          ) : (
            <Alert
              type="success"
              showIcon
              message="Looks complete — review and create."
            />
          )}

          {result?.missing?.length > 1 && refineCount < MAX_REFINES ? (
            <div>
              <Text strong>Add or correct details, then refine</Text>
              <Input.TextArea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. two tickets — General ₹10 and VIP ₹20, members-only…"
                style={{ marginTop: 6 }}
              />
            </div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Button onClick={startOver}>Start over</Button>
            <div style={{ display: "flex", gap: 8 }}>
              {result?.missing?.length > 1 && refineCount < MAX_REFINES ? (
                <Button onClick={handleRefine}>Refine</Button>
              ) : null}
              <Button type="primary" onClick={handleCreate}>
                Create event
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Upload your event poster — the assistant reads it, fills in what it
            can from your past events, and opens the event form pre-filled for
            you to review.
          </Paragraph>

          {previewUrl ? (
            <div style={{ textAlign: "center" }}>
              <Image
                src={previewUrl}
                alt="Poster preview"
                style={{ maxHeight: 220, borderRadius: 12, objectFit: "cover" }}
              />
              <div style={{ marginTop: 8 }}>
                <Button size="small" onClick={startOver}>
                  Choose a different image
                </Button>
              </div>
            </div>
          ) : (
            <Dragger
              accept="image/*"
              multiple={false}
              showUploadList={false}
              beforeUpload={pickFile}
            >
              <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                <ImageUp size={34} className="text-foreground/60" />
              </p>
              <Title level={5} style={{ margin: 0 }}>
                Upload event poster
              </Title>
              <Text type="secondary">
                PNG or JPG. We&apos;ll set it as your event cover.
              </Text>
            </Dragger>
          )}

          <div>
            <Text strong>Anything to add? (optional)</Text>
            <Input.TextArea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. members-only, two ticket tiers, parking available…"
              style={{ marginTop: 6 }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" disabled={!file} onClick={handleGenerate}>
              Generate event
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default PosterUploadModal;
